// Built-in trading tutorials for every indicator + drawing concept.
// Goal: a trader can learn how to use each tool WITHOUT leaving VTS.
// Keyed to IND_DEFS types where applicable, plus drawing concepts
// (fib, support_resistance, trendlines, volume).

export const TUTORIALS = {
  ema: {
    name: "EMA — Exponential Moving Average",
    what: "A moving average that weights recent prices more heavily, so it reacts faster to new price action than a simple average.",
    why: "Traders use it to read trend direction and dynamic support/resistance. Price above a rising EMA = uptrend; below a falling EMA = downtrend.",
    buy: "Price crosses and holds above the EMA, or a fast EMA (e.g. 9) crosses above a slow EMA (e.g. 21) — a bullish cross.",
    sell: "Price closes below the EMA, or the fast EMA crosses below the slow EMA — a bearish cross.",
    forex: "9 & 21 for scalping (M1–M5); 50 & 200 for swing/trend on H1–H4.",
    vi: "On Volatility Indices use 20 & 50 — they whip less on the synthetic noise than ultra-fast settings.",
    timeframe: "M5–H1 for intraday; H4–D1 for trend trades.",
    advantages: ["Reacts quickly to new trends", "Great for trend confirmation", "Doubles as dynamic support/resistance"],
    disadvantages: ["Lags real reversals", "Whipsaws badly in sideways markets"],
    mistakes: ["Trading every cross in a range", "Using only one EMA with no confirmation", "Ignoring the higher-timeframe trend"],
    example: "Price pulls back to a rising 21 EMA, prints a bullish engulfing candle, then continues up → buy the bounce with stop below the EMA.",
    market: "Trending",
    pairsWith: ["RSI", "MACD", "Support & Resistance"],
  },
  sma: {
    name: "SMA — Simple Moving Average",
    what: "The plain average of the last N closing prices, plotted as a smooth line.",
    why: "It smooths noise to reveal the underlying trend and marks widely-watched levels (the 50 & 200 SMA move markets).",
    buy: "Price holds above the SMA and the slope turns up; a 50/200 'golden cross' is a strong bullish signal.",
    sell: "Price breaks below the SMA with a downward slope; a 50/200 'death cross' is bearish.",
    forex: "20 for short-term, 50 for intraday trend, 200 for the major trend filter.",
    vi: "50 & 100 — slower SMAs ride the synthetic trends without over-reacting.",
    timeframe: "H1–D1; the 200 SMA is most respected on H4 and Daily.",
    advantages: ["Very smooth, fewer false signals than EMA", "Institutional levels (50/200) are self-fulfilling"],
    disadvantages: ["Slower than EMA — later entries", "Poor in choppy markets"],
    mistakes: ["Expecting fast scalp signals from a slow SMA", "Counter-trading a strong 200-SMA trend"],
    example: "A pullback to the 50 SMA in an uptrend that holds and bounces is a classic continuation entry.",
    market: "Trending",
    pairsWith: ["EMA", "ADX", "Volume"],
  },
  vwap: {
    name: "VWAP — Volume Weighted Average Price",
    what: "The average price weighted by volume over the session — the 'fair value' line institutions benchmark against.",
    why: "Price above VWAP = buyers in control; below = sellers. Intraday traders fade extremes back to VWAP or trade breakouts from it.",
    buy: "Price reclaims VWAP from below, or pulls back to VWAP in an uptrend and bounces.",
    sell: "Price rejects VWAP from above, or loses VWAP and retests it as resistance.",
    forex: "Use the session VWAP (London/NY open) — best on M1–M15 intraday.",
    vi: "On synthetics VWAP uses a range proxy (no true volume on Deriv) — treat it as a mean-reversion anchor, not gospel.",
    timeframe: "M1–M15 intraday only — VWAP resets each session.",
    advantages: ["Shows institutional fair value", "Excellent intraday mean-reversion anchor"],
    disadvantages: ["Only meaningful intraday", "Needs real volume — approximate on synthetics"],
    mistakes: ["Using VWAP on daily/swing charts", "Fading a strong trend that's riding above VWAP"],
    example: "In a NY-session uptrend, price dips to VWAP, holds, and resumes higher → buy the VWAP retest.",
    market: "Ranging / intraday trend",
    pairsWith: ["RSI", "Bollinger Bands", "Support & Resistance"],
  },
  boll: {
    name: "Bollinger Bands",
    what: "A moving average with an upper and lower band set 2 standard deviations away — they expand in volatility and contract in calm.",
    why: "Bands measure volatility and stretch. Price tends to revert from the outer bands; a 'squeeze' warns of an imminent breakout.",
    buy: "Price tags/closes outside the lower band then turns back up (mean-reversion), or breaks up out of a squeeze.",
    sell: "Price tags/closes outside the upper band then rolls over, or breaks down out of a squeeze.",
    forex: "20 period, 2.0 deviation (the default) on M15–H1.",
    vi: "20 period, 2.5 deviation — wider bands cope with synthetic spikes.",
    timeframe: "M15–H1 for reversions; any TF for squeeze breakouts.",
    advantages: ["Adapts to volatility automatically", "Great for range/mean-reversion", "Squeeze flags big moves"],
    disadvantages: ["'Walking the band' in strong trends fools reversion traders", "Bands are reactive, not predictive"],
    mistakes: ["Shorting every upper-band touch in a strong uptrend", "Ignoring the squeeze→expansion sequence"],
    example: "In a range, price spikes below the lower band, prints a pin bar, and snaps back to the middle band → buy the reversion.",
    market: "Ranging (reversion) or breakout (squeeze)",
    pairsWith: ["RSI", "VWAP", "ATR"],
  },
  supertrend: {
    name: "SuperTrend",
    what: "An ATR-based trend-following line that flips above price (sell) or below price (buy) as the trend changes.",
    why: "It gives a clean, binary trend read and a built-in trailing stop, removing guesswork about which way to trade.",
    buy: "The SuperTrend flips green and sits below price — go long and trail your stop on the line.",
    sell: "The SuperTrend flips red and sits above price — go short and trail on the line.",
    forex: "ATR period 10, multiplier 3 (default) on M15–H1.",
    vi: "ATR period 10, multiplier 4 — a wider band avoids constant flips on volatile synthetics.",
    timeframe: "M15–H4; higher TFs give cleaner flips.",
    advantages: ["Crystal-clear trend signal", "Acts as an automatic trailing stop", "Easy for beginners"],
    disadvantages: ["Many false flips in ranges", "Late on sharp V-reversals"],
    mistakes: ["Trading flips inside a tight range", "No higher-timeframe trend filter"],
    example: "SuperTrend flips below price after a pullback; you buy and trail your stop on the line until it flips red.",
    market: "Trending",
    pairsWith: ["ADX", "EMA", "RSI"],
  },
  rsi: {
    name: "RSI — Relative Strength Index",
    what: "A 0–100 momentum oscillator measuring the speed and size of recent price changes.",
    why: "It spots overbought (>70) and oversold (<30) conditions, momentum shifts, and divergences that warn of reversals.",
    buy: "RSI turns up from below 30 (oversold), or bullish divergence forms (price lower low, RSI higher low).",
    sell: "RSI turns down from above 70 (overbought), or bearish divergence (price higher high, RSI lower high).",
    forex: "14 period; use 30/70 bands. In strong trends use 40/80 or 20/60 to stay with the trend.",
    vi: "14 period with 20/80 bands — synthetics push extremes harder.",
    timeframe: "M15–H4. Divergences are most reliable on H1+.",
    advantages: ["Excellent reversal & divergence tool", "Universal, works on any market"],
    disadvantages: ["Stays overbought/oversold for long in strong trends", "Early signals get run over by momentum"],
    mistakes: ["Buying just because RSI<30 in a downtrend", "Ignoring trend context"],
    example: "Price makes a lower low but RSI makes a higher low (bullish divergence) at support → buy the reversal.",
    market: "Ranging (extremes) + Trending (divergence)",
    pairsWith: ["EMA", "Support & Resistance", "MACD"],
  },
  macd: {
    name: "MACD — Moving Average Convergence Divergence",
    what: "The difference between two EMAs (12/26) plus a 9-period signal line and a histogram of the gap between them.",
    why: "It reads trend + momentum together: crosses signal shifts, the histogram shows momentum building or fading, and divergence warns of reversals.",
    buy: "MACD line crosses above the signal line (especially below zero turning up), or histogram flips positive.",
    sell: "MACD line crosses below the signal line (especially above zero turning down), or histogram flips negative.",
    forex: "12, 26, 9 (default) on M30–H4.",
    vi: "12, 26, 9 works; on fast synthetics confirm with price structure to cut noise.",
    timeframe: "H1–H4 for clean crosses; M15 for scalps (more noise).",
    advantages: ["Combines trend and momentum", "Divergence catches reversals early"],
    disadvantages: ["Lags in fast markets", "Whipsaws in ranges"],
    mistakes: ["Trading every cross regardless of trend", "Reading the histogram without the zero line"],
    example: "After a downtrend, the MACD crosses up through its signal line below zero while price breaks a trendline → momentum-shift buy.",
    market: "Trending",
    pairsWith: ["EMA", "RSI", "Trendlines"],
  },
  stoch: {
    name: "Stochastic Oscillator",
    what: "Compares the close to the recent high-low range on a 0–100 scale with %K and %D lines.",
    why: "It's a fast momentum/reversal tool, great for timing entries within a range or pullbacks within a trend.",
    buy: "%K crosses above %D from below 20 (oversold) — momentum turning up.",
    sell: "%K crosses below %D from above 80 (overbought) — momentum turning down.",
    forex: "14,3,3 default; use 5,3,3 for faster scalps.",
    vi: "14,3,3 with 20/80 — avoid the ultra-fast 5,3,3 on noisy synthetics.",
    timeframe: "M5–H1.",
    advantages: ["Fast, precise entry timing", "Clear cross signals"],
    disadvantages: ["Very noisy — many false signals", "Stays pinned in strong trends"],
    mistakes: ["Trading crosses against a strong trend", "Using it standalone with no filter"],
    example: "In an uptrend, price pulls back, Stochastic dips below 20 then crosses up → buy the pullback.",
    market: "Ranging",
    pairsWith: ["EMA", "RSI", "Support & Resistance"],
  },
  adx: {
    name: "ADX — Average Directional Index",
    what: "Measures trend STRENGTH (0–100), regardless of direction, often with +DI/−DI direction lines.",
    why: "It tells you WHEN to trend-trade vs range-trade. Rising ADX = strong trend; low/falling ADX = chop to avoid.",
    buy: "ADX > 25 and rising with +DI above −DI — a strong uptrend worth following.",
    sell: "ADX > 25 and rising with −DI above +DI — a strong downtrend.",
    forex: "14 period; treat >25 as 'trend on', <20 as 'range/avoid'.",
    vi: "14 period; require >30 on synthetics since they fake trends often.",
    timeframe: "H1–H4 as a regime filter.",
    advantages: ["Best trend-strength filter", "Keeps you out of chop"],
    disadvantages: ["Gives no entry timing or direction by itself", "Lags"],
    mistakes: ["Using ADX value as a buy/sell signal", "Trend-trading when ADX is below 20"],
    example: "ADX rises through 25 as +DI crosses above −DI → confirm the new uptrend and trade pullbacks long.",
    market: "Filter for both",
    pairsWith: ["EMA", "SuperTrend", "MACD"],
  },
  atr: {
    name: "ATR — Average True Range",
    what: "Measures average volatility (the typical range a price moves per bar) — not direction.",
    why: "Used to size stops and targets to current volatility: wider stops in volatile markets, tighter in calm ones.",
    buy: "ATR is not a buy/sell signal — it sets your stop. Rising ATR confirms a real breakout has energy.",
    sell: "Same — use it to place stops (e.g. 1.5×ATR from entry), not as a direction trigger.",
    forex: "14 period; stop = 1.5–2× ATR; target = 2–3× ATR.",
    vi: "14 period; use 2–3× ATR stops — synthetics spike, so give trades room.",
    timeframe: "Any — it adapts to the chart's timeframe.",
    advantages: ["Volatility-adaptive stops", "Confirms breakout strength"],
    disadvantages: ["No direction", "Spikes on news/gaps"],
    mistakes: ["Using a fixed pip stop instead of ATR", "Treating ATR as a signal line"],
    example: "Place your stop 1.5×ATR below entry so normal noise doesn't stop you out, but a real reversal does.",
    market: "Both (risk tool)",
    pairsWith: ["Bollinger Bands", "SuperTrend", "Risk/Reward tool"],
  },
  ichimoku: {
    name: "Ichimoku Cloud",
    what: "A complete system: Tenkan, Kijun, the Cloud (Senkou A/B) and Chikou — it shows trend, momentum, and support/resistance at once.",
    why: "One glance tells you trend (price vs cloud), momentum (Tenkan/Kijun cross) and future S/R (cloud ahead).",
    buy: "Price above a green cloud, Tenkan crosses above Kijun, and Chikou is above price — full bullish alignment.",
    sell: "Price below a red cloud, Tenkan crosses below Kijun, and Chikou below price — full bearish alignment.",
    forex: "9, 26, 52 (default) on H1–D1.",
    vi: "9, 26, 52 still works; rely on the cloud for trend and ignore minor crosses on noisy synthetics.",
    timeframe: "H1–D1 — Ichimoku shines on higher timeframes.",
    advantages: ["All-in-one trend system", "Forward-projected support/resistance"],
    disadvantages: ["Looks cluttered to beginners", "Lags in fast markets"],
    mistakes: ["Trading against the cloud", "Acting on a cross while price is inside the cloud (no-trade zone)"],
    example: "Price breaks up out of the cloud as Tenkan crosses Kijun and Chikou is clear of price → high-confidence buy.",
    market: "Trending",
    pairsWith: ["RSI", "Support & Resistance"],
  },
  cci: {
    name: "CCI — Commodity Channel Index",
    what: "Measures how far price is from its statistical mean; typically oscillates between −100 and +100 but can spike far beyond.",
    why: "Spots overbought/oversold extremes and momentum surges; +100/−100 crosses flag new trends or reversions.",
    buy: "CCI crosses above −100 from below (reversion) or above +100 (momentum breakout).",
    sell: "CCI crosses below +100 from above (reversion) or below −100 (momentum breakdown).",
    forex: "14 or 20 period on M15–H1.",
    vi: "20 period with ±150 thresholds to filter synthetic noise.",
    timeframe: "M15–H1.",
    advantages: ["Catches strong momentum bursts", "Flexible reversion + breakout tool"],
    disadvantages: ["Unbounded — extremes can keep extending", "Noisy"],
    mistakes: ["Fading a +100 breakout in a strong trend", "No trend filter"],
    example: "CCI rockets above +100 as price breaks resistance → momentum buy; trail until CCI falls back under +100.",
    market: "Both",
    pairsWith: ["EMA", "ADX"],
  },
  psar: {
    name: "Parabolic SAR",
    what: "Dots that trail price, flipping from below (uptrend) to above (downtrend) — a stop-and-reverse trailing system.",
    why: "Provides an automatic trailing stop and a simple trend-flip signal.",
    buy: "Dots flip to below price — trend turned up; trail your stop on the dots.",
    sell: "Dots flip to above price — trend turned down.",
    forex: "Step 0.02, max 0.2 (default) on M15–H1.",
    vi: "Step 0.01, max 0.1 — slower acceleration reduces constant flips on synthetics.",
    timeframe: "M15–H1 in trending conditions.",
    advantages: ["Effortless trailing stop", "Clear flip signals"],
    disadvantages: ["Terrible in ranges (constant flips)", "Late on sharp reversals"],
    mistakes: ["Using PSAR in sideways markets", "Ignoring trend strength (no ADX filter)"],
    example: "In an uptrend, ride the trade and move your stop to each new SAR dot until the dots flip above price.",
    market: "Trending",
    pairsWith: ["ADX", "EMA"],
  },
  donchian: {
    name: "Donchian Channels",
    what: "Plots the highest high and lowest low over N bars — a classic breakout channel (the original 'Turtle' tool).",
    why: "Breakouts of the channel signal new trends; the midline acts as mean/trailing reference.",
    buy: "Price breaks above the upper channel (new N-bar high) — breakout long.",
    sell: "Price breaks below the lower channel (new N-bar low) — breakout short.",
    forex: "20 period on H1–H4.",
    vi: "20–50 period; wider channels reduce false breakouts on synthetics.",
    timeframe: "H1–D1 for trend breakouts.",
    advantages: ["Objective breakout levels", "Great trend-following entries"],
    disadvantages: ["False breakouts in ranges", "Late entries by design"],
    mistakes: ["Chasing breakouts in a tight range", "No volatility/volume confirmation"],
    example: "Price closes above the 20-bar high after a consolidation → breakout buy, stop below the channel midline.",
    market: "Trending / breakout",
    pairsWith: ["ATR", "ADX", "Volume"],
  },
  keltner: {
    name: "Keltner Channels",
    what: "An EMA with ATR-based upper/lower bands — similar to Bollinger but volatility comes from ATR, giving smoother bands.",
    why: "Cleaner trend & breakout read than Bollinger; riding the upper band signals strong trend.",
    buy: "Price breaks and holds above the upper band (trend) or bounces off the lower band toward the mid (reversion).",
    sell: "Price breaks below the lower band, or rejects the upper band back to the mid.",
    forex: "20 EMA, ATR×2 on M15–H1.",
    vi: "20 EMA, ATR×2.5 for synthetics.",
    timeframe: "M15–H1.",
    advantages: ["Smoother than Bollinger", "Good trend + squeeze tool"],
    disadvantages: ["Less reactive to sudden spikes", "Still whipsaws in chop"],
    mistakes: ["Confusing it with Bollinger reversion logic in trends", "No trend filter"],
    example: "Price rides along the upper Keltner band in a strong uptrend — stay long, only exit when it closes back to the mid EMA.",
    market: "Trending",
    pairsWith: ["ADX", "Bollinger Bands"],
  },
  williams: {
    name: "Williams %R",
    what: "A momentum oscillator from 0 to −100 showing where the close sits in the recent range (inverse-style Stochastic).",
    why: "Quick overbought (−20) / oversold (−80) reads for timing reversals and pullback entries.",
    buy: "%R rises from below −80 (oversold) back up.",
    sell: "%R falls from above −20 (overbought).",
    forex: "14 period on M5–H1.",
    vi: "14 period with −10/−90 bands to filter synthetic noise.",
    timeframe: "M5–H1.",
    advantages: ["Fast reversal timing", "Simple to read"],
    disadvantages: ["Noisy", "Pins at extremes in strong trends"],
    mistakes: ["Fading a strong trend on an extreme reading", "Standalone use"],
    example: "In a range, %R dips below −80 then turns up at support → buy the bounce.",
    market: "Ranging",
    pairsWith: ["EMA", "Support & Resistance"],
  },
  momentum: {
    name: "Momentum",
    what: "Measures the rate of price change over N bars — how fast price is moving and in which direction.",
    why: "Confirms trend strength and warns of exhaustion when momentum diverges from price.",
    buy: "Momentum crosses above its zero/baseline and rises — accelerating up.",
    sell: "Momentum crosses below the baseline and falls — accelerating down.",
    forex: "10–14 period on M15–H1.",
    vi: "14 period; confirm with price structure on synthetics.",
    timeframe: "M15–H1.",
    advantages: ["Early momentum read", "Good divergence tool"],
    disadvantages: ["Noisy", "No fixed bounds"],
    mistakes: ["Trading the line without price context", "Ignoring divergence"],
    example: "Price grinds to a new high but Momentum makes a lower high (divergence) → caution / take profits.",
    market: "Trending",
    pairsWith: ["MACD", "RSI"],
  },
  obv: {
    name: "OBV — On-Balance Volume",
    what: "A running total that adds volume on up days and subtracts it on down days — tracks buying vs selling pressure.",
    why: "Confirms trends (OBV should rise with price) and flags divergences when volume disagrees with price.",
    buy: "OBV makes higher highs with price (confirmed uptrend) or rises while price is flat (accumulation).",
    sell: "OBV makes lower lows with price, or falls while price holds (distribution).",
    forex: "Default; most useful as a confirmation overlay.",
    vi: "On Deriv synthetics OBV uses a range proxy (no true volume) — treat as approximate confirmation only.",
    timeframe: "H1–D1.",
    advantages: ["Reveals hidden accumulation/distribution", "Strong divergence signals"],
    disadvantages: ["Needs real volume to be precise", "No price levels"],
    mistakes: ["Relying on OBV on volume-less synthetics", "Using it without price confirmation"],
    example: "Price chops sideways but OBV climbs steadily (accumulation) → anticipate an upside breakout.",
    market: "Trending",
    pairsWith: ["Volume", "Support & Resistance", "Trendlines"],
  },
  volume: {
    name: "Volume",
    what: "The number of units traded per bar — the fuel behind a move.",
    why: "Strong moves need strong volume. Breakouts on high volume tend to hold; on low volume they often fail.",
    buy: "Breakout above resistance on a clear volume surge — real demand.",
    sell: "Breakdown below support on high volume, or an up-move fading on shrinking volume.",
    forex: "Use tick volume as a proxy (FX has no central volume) on M15–H1.",
    vi: "Deriv synthetics have no true volume — VTS shows a bar-range proxy; treat it as relative activity, not exact.",
    timeframe: "Any — pair with the price level you're watching.",
    advantages: ["Confirms conviction behind moves", "Filters fake breakouts"],
    disadvantages: ["No FX/synthetic true volume (proxy only)", "No direction by itself"],
    mistakes: ["Trusting a breakout with no volume expansion", "Reading volume in isolation"],
    example: "Price breaks resistance on a volume spike 2–3× the average → the breakout has conviction; buy the retest.",
    market: "Both (confirmation)",
    pairsWith: ["Support & Resistance", "OBV", "Bollinger Bands"],
  },
  fib: {
    name: "Fibonacci Retracement",
    what: "Horizontal levels (23.6%, 38.2%, 50%, 61.8%, 78.6%) drawn across a swing to map likely pullback/reversal zones.",
    why: "Markets pull back in proportion before continuing. The 61.8% 'golden' level is a high-probability continuation zone.",
    buy: "In an uptrend, price pulls back to the 50–61.8% zone and prints a bullish reversal candle → buy the continuation.",
    sell: "In a downtrend, price retraces up to 50–61.8% and rejects → sell the continuation.",
    forex: "Draw swing-low → swing-high (uptrend). Watch 38.2/50/61.8 on H1–H4.",
    vi: "Same levels; combine with structure — synthetics respect the 50% and 61.8% well.",
    timeframe: "H1–D1 swings give the cleanest levels.",
    advantages: ["Maps precise entry zones", "Universally watched (self-fulfilling)"],
    disadvantages: ["Subjective swing selection", "Levels are zones, not exact lines"],
    mistakes: ["Drawing on random swings", "Treating a level as a guaranteed reversal with no confirmation"],
    example: "Uptrend pulls back to the 61.8% fib at prior structure, prints a hammer → buy with stop below the 78.6%.",
    market: "Trending (pullbacks)",
    pairsWith: ["Support & Resistance", "RSI", "Trendlines"],
  },
  support_resistance: {
    name: "Support & Resistance",
    what: "Horizontal price zones where buyers (support) or sellers (resistance) have repeatedly stepped in.",
    why: "They're the foundation of price action — where reversals, breakouts and stop placement all revolve.",
    buy: "Price reaches support and shows rejection (long wick / bullish engulfing) → buy with stop below support.",
    sell: "Price reaches resistance and rejects → sell with stop above resistance. A clean break + retest flips roles.",
    forex: "Mark zones from swing highs/lows and round numbers (1.1000, 150.00) on H1–H4.",
    vi: "Synthetics respect recent swing levels well — mark them on M15–H1.",
    timeframe: "Mark on H4/D1, trade on M15–H1.",
    advantages: ["The most reliable, universal concept", "Defines clear risk (stop) levels"],
    disadvantages: ["Zones, not exact lines", "Strong trends slice through levels"],
    mistakes: ["Drawing too many thin lines", "Ignoring the higher-timeframe trend", "No confirmation candle"],
    example: "Price drops to a daily support zone, prints a bullish pin bar, and turns up → buy with stop just below the zone.",
    market: "Both",
    pairsWith: ["RSI", "Fibonacci", "Volume"],
  },
  trendlines: {
    name: "Trendlines",
    what: "Diagonal lines connecting consecutive higher lows (up) or lower highs (down) to map the trend's slope.",
    why: "They show trend direction, act as dynamic support/resistance, and flag reversals when broken.",
    buy: "In an uptrend, price pulls back to a rising trendline and bounces → buy the touch with stop below the line.",
    sell: "In a downtrend, price rallies to a falling trendline and rejects → sell. A break + retest signals a reversal.",
    forex: "Connect at least 2 (ideally 3) swing points on H1–H4.",
    vi: "Same — synthetics form clean channels; trade the touches and breaks.",
    timeframe: "H1–H4 for the cleanest, most-respected lines.",
    advantages: ["Simple, powerful trend map", "Dynamic support/resistance + break signals"],
    disadvantages: ["Subjective (where to anchor)", "Can be redrawn to fit bias"],
    mistakes: ["Forcing a line through price", "Trading a 2-point line as gospel", "Ignoring the break-and-retest"],
    example: "Price respects a rising trendline 3 times, then on the 4th touch bounces with a bullish candle → buy the trendline.",
    market: "Trending",
    pairsWith: ["Support & Resistance", "MACD", "RSI"],
  },
};

// Topics shown in the Learn browser (drawing concepts first, then indicators).
export const TUTORIAL_ORDER = [
  "support_resistance", "trendlines", "fib",
  "ema", "sma", "vwap", "boll", "keltner", "donchian", "supertrend", "psar", "ichimoku",
  "rsi", "macd", "stoch", "cci", "williams", "momentum", "adx", "atr",
  "volume", "obv",
];

// How to actually TRADE each one-tap strategy preset (keyed to PRESETS id in ProTrade).
export const STRATEGY_TUTORIALS = {
  ema25_100: {
    name: "EMA 25 / 100 — Trend Cross",
    idea: "A fast/slow EMA pair that keeps you on the right side of the intraday trend and signals shifts when they cross.",
    indicators: ["EMA 25 (fast)", "EMA 100 (slow)"],
    howto: [
      "Add the preset — two EMAs appear on the chart.",
      "Read the trend: price above both rising EMAs = uptrend; below both falling = downtrend.",
      "Trade pullbacks to the fast EMA in the direction of the cross.",
    ],
    buy: "EMA 25 crosses ABOVE EMA 100 (bullish cross) and price holds above both — buy the first pullback to the 25 EMA.",
    sell: "EMA 25 crosses BELOW EMA 100 (bearish cross) and price stays below — sell the first pullback to the 25 EMA.",
    exit: "Exit when EMA 25 crosses back through EMA 100, or trail your stop under each higher low (longs).",
    market: "Trending", timeframe: "M1–M15 (intraday)",
    tips: ["Only take crosses that agree with the higher-timeframe trend", "Skip signals when the EMAs are flat & tangled (range)"],
    mistakes: ["Trading every cross in a sideways market", "Entering at the cross instead of waiting for the pullback"],
  },
  golden50_200: {
    name: "Golden Cross 50 / 200 — Major Trend",
    idea: "The classic institutional trend filter. The 50/200 cross defines the dominant market direction.",
    indicators: ["EMA 50", "EMA 200"],
    howto: [
      "Apply the preset on H1–H4.",
      "Golden Cross (50 above 200) = bull regime; Death Cross (50 below 200) = bear regime.",
      "Only take trades in the regime's direction; use the 50 EMA as dynamic support/resistance for entries.",
    ],
    buy: "After a Golden Cross, buy pullbacks that hold the 50 EMA with a bullish reversal candle.",
    sell: "After a Death Cross, sell rallies that reject the 50 EMA.",
    exit: "Exit on the opposite cross or when price closes decisively through the 200 EMA against you.",
    market: "Trending (swing)", timeframe: "H1–D1",
    tips: ["Great as a bias filter for faster strategies", "The 200 EMA is a powerful line — many traders defend it"],
    mistakes: ["Counter-trading a strong regime", "Using it for scalps — it's a slow, big-picture tool"],
  },
  scalper: {
    name: "Scalper — EMA 9 + 21 + RSI",
    idea: "Fast momentum scalping: ride short bursts in the EMA-trend direction, timed with RSI.",
    indicators: ["EMA 9 (fast)", "EMA 21 (slow)", "RSI 14"],
    howto: [
      "Use on M1–M5 in an active session.",
      "Direction = EMA 9 vs EMA 21. Trade only with that slope.",
      "Use RSI to time the entry on small pullbacks.",
    ],
    buy: "EMA 9 above EMA 21 (uptrend) and RSI dips toward 40–50 then turns up → buy the pullback.",
    sell: "EMA 9 below EMA 21 (downtrend) and RSI bounces to 50–60 then rolls over → sell the pullback.",
    exit: "Take quick profits at 1–1.5R; exit if EMA 9 crosses back through EMA 21. Keep stops tight.",
    market: "Trending (fast)", timeframe: "M1–M5",
    tips: ["Trade only during high-liquidity sessions (London/NY)", "Avoid scalping into major news"],
    mistakes: ["Over-trading in chop", "Holding scalps too long", "Loose stops"],
  },
  meanrev: {
    name: "Mean Reversion — Bollinger 20 + RSI",
    idea: "Fade stretched moves back to the mean inside a range — buy panic, sell euphoria.",
    indicators: ["Bollinger Bands 20", "RSI 14"],
    howto: [
      "Use only when the market is RANGING (flat Bollinger middle).",
      "Watch for price tagging an outer band while RSI is at an extreme.",
      "Enter on the reversal candle, target the middle band.",
    ],
    buy: "Price tags the LOWER band while RSI < 30, then prints a bullish reversal → buy, target the middle band.",
    sell: "Price tags the UPPER band while RSI > 70, then rolls over → sell, target the middle band.",
    exit: "Take profit at the middle (or opposite) band; stop just beyond the band you faded.",
    market: "Ranging", timeframe: "M15–H1",
    tips: ["Confirm the market is ranging before fading", "Tighter is better — exit fast if momentum continues"],
    mistakes: ["Fading a strong trend (price 'walks the band')", "No stop beyond the band"],
  },
  momentum: {
    name: "Momentum — EMA 20 + MACD",
    idea: "Catch and ride accelerating moves: EMA 20 sets direction, MACD confirms momentum.",
    indicators: ["EMA 20", "MACD (12,26,9)"],
    howto: [
      "Direction = price vs the 20 EMA.",
      "Confirmation = MACD cross + histogram expanding in that direction.",
      "Enter on the MACD trigger aligned with the EMA trend.",
    ],
    buy: "Price above the 20 EMA and MACD crosses up through its signal with a rising histogram → buy.",
    sell: "Price below the 20 EMA and MACD crosses down with a falling histogram → sell.",
    exit: "Exit when the MACD histogram flips against you or price loses the 20 EMA.",
    market: "Trending", timeframe: "M15–H1",
    tips: ["Strongest when the MACD cross happens near the zero line", "Add a higher-TF trend filter for quality"],
    mistakes: ["Trading MACD crosses against the EMA trend", "Chasing after the histogram already peaked"],
  },
  trendmom: {
    name: "Trend + Momentum — EMA 50/200 + MACD",
    idea: "Combine a robust trend filter (50/200) with a MACD momentum trigger for high-quality swing entries.",
    indicators: ["EMA 50", "EMA 200", "MACD (12,26,9)"],
    howto: [
      "Establish bias: 50 above 200 = longs only; 50 below 200 = shorts only.",
      "Wait for a pullback toward the 50 EMA.",
      "Trigger with a MACD cross in the trend direction.",
    ],
    buy: "Uptrend (50>200), price pulls back to the 50 EMA, MACD crosses up → buy.",
    sell: "Downtrend (50<200), price rallies to the 50 EMA, MACD crosses down → sell.",
    exit: "Trail under swing lows (longs); exit on a MACD cross against you or a 50/200 flip.",
    market: "Trending (swing)", timeframe: "H1–H4",
    tips: ["The two-filter combo cuts false signals dramatically", "Be patient — fewer but cleaner setups"],
    mistakes: ["Forcing trades when price is far from the 50 EMA", "Ignoring the 50/200 bias"],
  },
};
